import React from 'react';
import { useField, useFormikContext } from 'formik';
import * as Yup from 'yup';
import TextField from '../Form/TextField';
import StateSelectBox from '../Form/StateSelectBox';
import { PhoneField, SsnField, ZipField } from '../Form/MaskedFields';
import Icd10Picker from './Icd10Picker';
import ProviderSelect from './ProviderSelect';
import LegacyInsurance from './LegacyInsurance';
import { normalizeState, normalizePhone, digitsOnly, programsFor, programRuleErrors, providerMissingWarning } from '../../utils/patientForm';

const col = 'col-lg-4 col-md-4 col-sm-6 col-xs-12';

/** Yup rules shared by every Add Patient form (mirrors backend patientForm.prepare). */
export const patientCoreSchema = {
    phone1: Yup.string().test('phone10', 'Phone must be 10 digits', (v) => !v || normalizePhone(v).length === 10),
    address: Yup.string(),
    line2: Yup.string(),
    city: Yup.string().trim().required('City is required'),
    state: Yup.string().test('usps', 'Pick a state from the list', (v) => !v || normalizeState(v) !== null),
    zipCode: Yup.string().test('zip5', 'ZIP must be 5 digits', (v) => !v || digitsOnly(v).length === 5),
    ssn: Yup.string().test('ssn9', 'SSN must be 9 digits', (v) => !v || digitsOnly(v).length === 9),
    diagnosisCodes: Yup.array().of(Yup.object({ code: Yup.string().required() })),
};
export const patientCoreInitial = { phone1: '', address: '', line2: '', city: '', state: '', zipCode: '', ssn: '', diagnosisCodes: [] };

/** Program-rule check for submit; returns an error string or null. */
export const diagnosisRuleError = (values) => {
    const errs = programRuleErrors(programsFor(values.patientType || 'RPM'), (values.diagnosisCodes || []).length);
    return errs.length ? errs.join('; ') : null;
};

/** Converts the form values to the API payload (codes only; normalized contact fields). */
export const toPatientPayload = (values) => {
    const out = { ...values };
    out.diagnosisCodes = (values.diagnosisCodes || []).map((d) => d.code);
    if (out.phone1) out.phone1 = normalizePhone(out.phone1);
    if (out.zipCode) out.zipCode = digitsOnly(out.zipCode);
    if (out.ssn) out.ssn = digitsOnly(out.ssn);
    if (out.state) out.state = normalizeState(out.state) || out.state;
    delete out.diseases;
    delete out.insurancecompany;
    return out;
};

const FormikIcd10 = ({ name = 'diagnosisCodes', ...rest }) => {
    const [field, meta, helpers] = useField(name);
    return <Icd10Picker value={field.value || []} onChange={(v) => helpers.setValue(v)} error={meta.touched || meta.error ? meta.error : null} {...rest} />;
};

/** Address/contact/SSN, ICD-10 diagnoses, legacy-insurance note and (optionally) provider for Add Patient forms. */
const PatientCoreFields = ({ orgId, providerId, onProviderChange, showPhone = true, patientType }) => {
    const { values } = useFormikContext();
    const programs = programsFor(patientType || values.patientType || 'RPM');
    return (
        <>
            {showPhone ? <div className={col}><PhoneField label="Phone" name="phone1" placeholder="(XXX) XXX-XXXX" /></div> : null}
            <div className={col}><TextField label="Address" name="address" type="text" placeholder="Address" /></div>
            <div className={col}><TextField label="Address 2" name="line2" type="text" placeholder="Apt, suite, unit" /></div>
            <div className={col}><TextField label="City" name="city" type="text" placeholder="City" compulsory={true} /></div>
            <div className={col}><StateSelectBox label="State" name="state" placeholder="Select or type a state..." /></div>
            <div className={col}><ZipField label="ZIP" name="zipCode" placeholder="5 digits" /></div>
            <div className={col}><SsnField label="SSN" name="ssn" placeholder="XXX-XX-XXXX" /></div>
            <div className="col-lg-8 col-md-8 col-sm-12 col-xs-12">
                <FormikIcd10 compulsory label={`Diagnoses (ICD-10-CM) — ${programRuleErrors(programs, 0).join('; ') || 'optional'}`} />
            </div>
            {onProviderChange ? (
                <div className={col}>
                    <ProviderSelect organizationId={orgId} value={providerId} onChange={onProviderChange} warning={providerMissingWarning(programs, providerId)} />
                </div>
            ) : null}
            <div className={col}><LegacyInsurance /></div>
        </>
    );
};

export default PatientCoreFields;
